import { useEffect, useRef } from 'react';
import { useKeyboardShortcuts, type ShortcutAction } from '../contexts/keyboardShortcuts/keyboardShortcuts.context';

/**
 * Hook to register a keyboard shortcut for a component
 * 
 * @param shortcut - The keyboard shortcut (e.g., 'shift+s', 'ctrl+k')
 * @param handler - Function to execute when shortcut is pressed
 * @param dependencies - Dependencies array (like useEffect)
 * @param description - Description of what the shortcut does
 * @param scope - Whether the shortcut is 'global' or 'page' scoped
 */
export const useKeyboardShortcut = (
  shortcut: string,
  handler: () => void,
  dependencies: React.DependencyList = [],
  description: string = '',
  scope: 'global' | 'page' = 'page'
) => {
  const { registerShortcut } = useKeyboardShortcuts();
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Clean up previous registration
    if (cleanupRef.current) {
      cleanupRef.current();
    }

    // Register new shortcut
    const shortcutAction: ShortcutAction = {
      key: shortcut,
      description: description || `Shortcut: ${shortcut}`,
      handler,
      scope
    };

    cleanupRef.current = registerShortcut(shortcutAction);

    // Cleanup on unmount
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [shortcut, description, scope, registerShortcut, ...dependencies]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);
};
